import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import axios from 'axios'

import { generateApiPath } from '@taxfix/scouts-helpers'

import { handleRequestError } from '@/actions'
import { ERROR_MESSAGE_AUTHENTICATION, ERROR_MESSAGE_SAVING } from '@/constants'
import { getHashedTrackingData } from '@/helpers/get-hashed-tracking-data'
import { deleteUnregisteredUserToken, getUnregisteredUserToken } from '@/helpers/unregistered-user'
import { ProfileDTO, ProfileSlice } from '@/models/profile'
import { track, TRACK_USER_AUTHENTICATE } from '@/trackers'

import { ENDPOINTS } from '../endpoints'
import { store, AppDispatch, RootState } from '../store'
import { clearExtraProfileCookieData, getExtraProfileCookieData, ExtraCookieData } from './helpers'

type PartialProfileSlice = Partial<ProfileSlice> | undefined

type AsyncThunkFields = { dispatch: AppDispatch; state: RootState }

const initialState: PartialProfileSlice = {
    fetchSuccessful: null,
}

export const profileSlice = createSlice({
    name: 'profile',
    initialState,
    reducers: {
        receiveProfile: (state, action: PayloadAction<PartialProfileSlice>) => ({ ...state, ...action.payload }),
    },
    extraReducers: (builder) => {
        builder
            .addCase(
                loggedin.fulfilled,
                (state, action: PayloadAction<{ profile: PartialProfileSlice }>): PartialProfileSlice => ({
                    ...state,
                    ...action.payload.profile,
                    fetchSuccessful: true,
                })
            )
            .addCase(
                requestProfile.fulfilled,
                (state, action: PayloadAction<PartialProfileSlice>): PartialProfileSlice => ({
                    ...state,
                    ...action.payload,
                    fetchSuccessful: true,
                })
            )
            .addCase(
                saveProfile.fulfilled,
                (state, action: PayloadAction<PartialProfileSlice>): PartialProfileSlice => ({
                    ...state,
                    ...action.payload,
                })
            )
    },
})

export const loggedin = createAsyncThunk<
    { profile: ProfileDTO; isSignUp: boolean; extraCookieData: ExtraCookieData; hashedEmail: string | undefined },
    PartialProfileSlice,
    AsyncThunkFields
>('profile/loggedin', async (data, { dispatch }) => {
    const extraCookieData = getExtraProfileCookieData()

    try {
        const url = ENDPOINTS.USER.POST.LOGGED_IN

        const unregisteredUserToken = getUnregisteredUserToken()
        const config = unregisteredUserToken
            ? { headers: { 'X-Unregistered-Authorization': `Bearer ${unregisteredUserToken}` } }
            : {}

        const response = await axios.post(url, { ...data, ...extraCookieData }, config)

        const { id: userId, email, variant, initialVariant, referralSource } = response.data

        track(TRACK_USER_AUTHENTICATE, { userId, email, variant, initialVariant, referralSource })

        const isSignUp = response.status === 201

        const { hashedEmail } = await getHashedTrackingData({ email })

        clearExtraProfileCookieData()

        if (unregisteredUserToken) {
            deleteUnregisteredUserToken()
        }

        return { profile: response.data, isSignUp, extraCookieData, hashedEmail }
    } catch (error) {
        // @TODO:
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dispatch(handleRequestError(error, { customMessage: ERROR_MESSAGE_AUTHENTICATION } as any))
        throw error
    }
})

export const enablePushNotifications = createAsyncThunk<void, string, AsyncThunkFields>(
    'profile/enablePushNotifications',
    async (token, { dispatch }) => {
        try {
            await axios.put(ENDPOINTS.PUSH_NOTIFICATIONS.PUT.ON, { token })
        } catch (error) {
            dispatch(handleRequestError(error))
        }
    }
)

export const disablePushNotifications = createAsyncThunk<void, string, AsyncThunkFields>(
    'profile/disablePushNotifications',
    async (token, { dispatch }) => {
        try {
            await axios.put(ENDPOINTS.PUSH_NOTIFICATIONS.PUT.OFF, { token })
        } catch (error) {
            dispatch(handleRequestError(error))
        }
    }
)

export const requestProfile = createAsyncThunk<ProfileDTO, undefined, AsyncThunkFields>(
    'profile/request',
    async (_, { getState }) => {
        const { profile } = getState()

        const url = generateApiPath(ENDPOINTS.USER.GET.SINGLE, { userId: profile.id })
        const response = await axios.get(url, { headers: { 'Cache-Control': 'no-cache' } })

        return response.data
    }
)

export const saveProfile = createAsyncThunk<ProfileDTO, PartialProfileSlice, AsyncThunkFields>(
    'profile/save',
    async (data, { dispatch, getState }) => {
        const { profile } = getState()

        try {
            const url = generateApiPath(ENDPOINTS.USER.PUT, { userId: profile.id })
            const response = await axios.put(url, { ...profile, ...data })

            return response.data
        } catch (error) {
            // @TODO:
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            dispatch(handleRequestError(error, { customMessage: ERROR_MESSAGE_SAVING } as any))
            throw error
        }
    }
)

export const updateAndSaveProfile = (data: PartialProfileSlice) => (dispatch: AppDispatch) => {
    dispatch(receiveProfile(data))
    return dispatch(saveProfile(data))
}

/**
 * Refetch profile data, which basically invalidates it.
 * When profile is finally converted to RTK Query, we should start correctly invalidating the profile tag instead.
 */
export const invalidateProfile = () => store.dispatch(requestProfile())

export const { receiveProfile } = profileSlice.actions

export const profileReducer = profileSlice.reducer
